import { AsyncPipe, NgClass } from '@angular/common';
import { ChangeDetectorRef, Component, HostBinding, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Fixture, GroupItemType } from '@cds/betting-offer';
import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import { SportsCacheService } from '@frontend/sports/betting-offer/feature/sports-cache';
import { CDNCachingConfig, GridConfig, MatchListConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { CountItem, ItemType, children } from '@frontend/sports/common/core/data-access/sport-model';
import { TrustPipe } from '@frontend/sports/common/core/utils/dom';
import { interpolateDoubleBracesString, interpolateSingleBracesString } from '@frontend/sports/common/core/utils/string';
import { CompetitionLogoComponent } from '@frontend/sports/competition-logos/feature';
import { GridGrouping } from '@frontend/sports/grid/core/feature/model';
import { ObservableGrid, ObservableGridFactory } from '@frontend/sports/grid/core/feature/observables';
import { IGridRootState } from '@frontend/sports/grid/core/feature/store';
import { ItemComponent } from '@frontend/sports/item/feature';
import { CustomLoadingIndicatorService, ILoadingIndicatorHandler, LoadingIndicatorModule } from '@frontend/sports/loading-indicator-feature';
import { UrlParam } from '@frontend/sports/navigation/core/feature/url-builder';
import { UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { TitleIconOptions } from '@frontend/sports/types/components/content';
import { WidgetContext, WidgetPage, WidgetType } from '@frontend/sports/types/components/widget';
import { TabbedGridLink, TabbedGridLinkTarget, TabbedGridPayload, TabbedGridType } from '@frontend/sports/types/components/widget/types';
import { PrimaryNavigationDisplayType } from '@frontend/sports/types/configuration';
import { ModularConfigAccessorService } from '@frontend/sports/widget/core/feature';
import { DeviceService } from '@frontend/vanilla/core';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { Store } from '@ngrx/store';
import { Observable, distinctUntilChanged, filter, finalize, map, of } from 'rxjs';

import { ErrorComponent } from '../common/error/error.component';
import { DropdownModule } from '../dropdown/dropdown.module';
import { CompetitionGridActions } from '../event-list-shared/sport/competitions/competition-grid.actions';
import { GridModule } from '../grid/grid.module';
import { SignpostTypeService } from '../shared-priceboost/services/signpost-type.service';
import { SignpostType } from '../shared-priceboost/signpost.model';
import { WidgetCommonModule } from '../widget/common/widget-common.module';
import { WidgetContextService } from '../widget/common/widget-context.service';
import { LinkConfig } from '../widget/common/widget-header.component';
import { WidgetNavigationService } from '../widget/common/widget-navigation.service';
import { WidgetPillComponent } from '../widget/common/widget-pill.component';
import { WidgetTabBarItem } from '../widget/common/widget-tab-bar.component';
import { ComposableParentPayload } from '../widget/composable/composable-widget.component';
import { WidgetComponent } from '../widget/core/widget-registry.service';
import { TabbedGridNavigationService, TabbedGridTabBarItem } from './tabbed-grid-navigation.service';
import { TabbedGridTrackingService } from './tabbed-grid-tracking.service';
import { MatchListItem } from './tabbed-grid.model';

const token = 'TABBED_GRID';

@Component({
    imports: [
        AsyncPipe,
        NgClass,
        WidgetCommonModule,
        LoadingIndicatorModule,
        CompetitionLogoComponent,
        GridModule,
        DropdownModule,
        ErrorComponent,
        ItemComponent,
        TrustPipe,
        WidgetPillComponent,
        IconCustomComponent,
    ],
    selector: 'ms-tabbed-grid-widget',
    templateUrl: './tabbed-grid-widget.component.html',
    providers: [TabbedGridTrackingService, SignpostTypeService],
})
export class TabbedGridWidgetComponent extends WidgetComponent<TabbedGridPayload> implements OnDestroy {
    @HostBinding('class') className = 'tabbed-grid-widget grid-wrapper';

    link?: string;
    linkCount?: number;
    linkTitle?: string;
    globalLink?: string;
    globalLinkTitle?: string;
    headerChevronLink: string | undefined;

    title: string;
    titleIcon: string;
    titleLogo: boolean;
    titleData?: MatchListItem;

    tracking: string;
    hideMarketSwitcher = false;
    hideGridTitle = true;
    stretchTab = false;
    hasMore = false;
    moreLoading = false;

    primaryNavigation: TabbedGridTabBarItem[] = [];
    secondaryNavigation: TabbedGridTabBarItem[] = [];
    showFirstLevelNavigation: boolean;
    /**
     * Indicates if parent widget (e.g. composable widget) is displaying navigation of tabbed grid.
     * In such case tabbed grid navigation is hidden and only content is displayed.
     */
    parentIsHandlingNavigation = false;

    grid: ObservableGrid;
    groupTypeCompetition = GroupItemType.Competition;
    primaryActiveTab?: TabbedGridTabBarItem;
    isAllTabEnabled: boolean;
    gridHeaderOverride?: string;
    headerLinkConfig: LinkConfig;
    showPrimaryNavAsPill = false;

    private gridType: TabbedGridType;
    private linkConfig?: TabbedGridLink;
    private globalLinkConfig: TabbedGridLink;
    private activeNavigation?: TabbedGridTabBarItem;
    private readonly shouldIncludePayload = this.cdnCachingConfig.widgets.includes(WidgetType.TabbedGrid);

    constructor(
        public sitecore: Sitecore,
        private gridConfig: GridConfig,
        private urlHelper: UrlHelperService,
        private deviceService: DeviceService,
        private matchListConfig: MatchListConfig,
        private gridFactory: ObservableGridFactory,
        private contextService: WidgetContextService,
        private trackingService: TabbedGridTrackingService,
        private loadingIndicatorService: CustomLoadingIndicatorService,
        private accessorService: ModularConfigAccessorService,
        private navigationService: TabbedGridNavigationService,
        private sportCache: SportsCacheService,
        private changeRef: ChangeDetectorRef,
        private store: Store<IGridRootState>,
        private signpostTypeService: SignpostTypeService,
        private cdnCachingConfig: CDNCachingConfig,
        private widgetNavigationService: WidgetNavigationService,
    ) {
        super();
        this.showPrimaryNavAsPill = this.gridConfig.primaryNavigationType === PrimaryNavigationDisplayType.Pill;
    }

    ngOnDestroy(): void {
        this.grid?.destroy();
    }

    protected override onData(data: TabbedGridPayload, isWidgetRefresh: boolean): void {
        if (!data?.fixtures?.length) {
            this.getData(isWidgetRefresh).subscribe(this.mapData);
        } else {
            this.mapData(data);
        }
    }

    private mapData = (payload: TabbedGridPayload) => {
        if (!payload?.fixtures?.length) {
            this.hasData = false;
            this.setWidgetLoaded();
            this.logEmptyData();

            return;
        }

        this.title = this.interpolateTitle(payload.title ?? '', payload.fixtures[0]);
        this.linkConfig = payload.bottomLink;
        this.hideMarketSwitcher = payload.hideMarketSwitcher;
        this.titleIcon = payload.titleIcon;
        this.titleLogo = payload.titleIcon === TitleIconOptions.CompetitionLogo;
        this.globalLinkConfig = payload.globalLink;
        this.gridType = payload.gridType;
        this.signpostTypeService.signpostType = this.gridType === TabbedGridType.CompetitionGrid ? SignpostType.PriceBoost : SignpostType.None;

        this.hasMore = payload.enablePaging ? payload.fixtures.length < (payload.navigation?.[0].count ?? 0) : false;

        this.setTabs(payload);
        this.setGrid(payload);
        this.setLink(payload);
        this.setTracking(payload.fixtures);
        this.setHeaderChevronLink(payload.showChevron, payload.fixtures[0]);

        this.titleData = this.getDataForTitleIcon(payload.fixtures[0]);
        this.setMarketSwitcherVisibility();
        this.setGlobalLink(payload.fixtures[0]);
        this.deviceService.orientation
            .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
            .subscribe((orientation) => this.setGridTitleVisibility(orientation));
        this.showFirstLevelNavigation = this.secondaryNavigation?.length ? this.primaryNavigation?.length > 0 : this.primaryNavigation?.length > 1;
        this.gridHeaderOverride = payload.gridHeaderOverride;
    };

    protected override onParentData(payload: ComposableParentPayload): void {
        this.parentIsHandlingNavigation = payload.mergeNavigation;
        this.isAllTabEnabled = payload.isAllTabEnabled;
    }

    get showPrimaryNavigation(): boolean {
        return this.showFirstLevelNavigation && !this.parentIsHandlingNavigation;
    }

    loadMore(): void {
        if (!this.hasMore || this.moreLoading) {
            return;
        }

        this.moreLoading = true;

        const currentCount = this.grid.getState().groups.reduce((total, group) => total + group.count, 0);

        this.contextService
            .getWidget<TabbedGridPayload>({ ...this.getContext(), skip: currentCount })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(({ payload }) => {
                if (payload) {
                    this.hasMore = currentCount + payload.fixtures.length < (payload.navigation?.[0].count ?? 0);

                    const grid = this.grid.getState();
                    this.store.dispatch(
                        CompetitionGridActions.addEvents({
                            payload: {
                                id: grid.id,
                                fixtures: payload.fixtures,
                            },
                        }),
                    );

                    this.moreLoading = false;
                }
            });
    }

    override getContext(): Partial<WidgetContext> {
        const context = super.getContext();

        return !this.shouldIncludePayload ? context : { ...context, shouldIncludePayload: this.shouldIncludePayload };
    }

    private getData(isWidgetRefresh: boolean): Observable<TabbedGridPayload> {
        return this.contextService.getWidget<TabbedGridPayload>(this.getContext(), !isWidgetRefresh).pipe(
            filter(({ payload }) => payload !== undefined),
            map(({ payload }) => payload as TabbedGridPayload),
            takeUntilDestroyed(this.destroyRef),
        );
    }

    primaryNavigationSelected(tabEvent: WidgetTabBarItem): void {
        if (tabEvent) {
            this.primaryNavigation = this.primaryNavigation.map((item) => ({
                ...item,
                active: item.id === tabEvent.id,
            }));
            this.setGridAndLinks(true);
        }
    }

    secondaryNavigationSelected(): void {
        this.setGridAndLinks(false);
    }

    linkClick(isGlobalLink: boolean = false): void {
        this.trackingService.trackLinkClick({
            eventDetails: isGlobalLink ? 'global link' : 'show more',
            urlClicked: isGlobalLink ? this.globalLink : this.link,
        });
    }

    eventClicked({ event, index }: { event: EventModel; index: number }): void {
        this.trackingService.trackEventClick(event, index);
    }

    get showMoreMessage(): string {
        return this.moreLoading ? '' : this.sitecore.event.MoreEvents;
    }

    private setMarketSwitcherVisibility(): void {
        if (!this.deviceService.isMobile || (this.deviceService.isTablet && this.deviceService.currentOrientation === 'landscape')) {
            this.hideMarketSwitcher = false;
        } else if (this.secondaryNavigation.length) {
            this.hideMarketSwitcher = true;
        }
    }

    private setGrid({ fixtures, disabledFixtureSorting, gridType, showNonGridable }: TabbedGridPayload): void {
        const generateGrid = (moreGrouping?: boolean) => {
            this.grid?.destroy();

            if (!fixtures?.length) {
                return;
            }

            const primaryActive = this.getPrimaryActive();
            const secondaryActive = this.getSecondaryActive();
            this.primaryActiveTab = primaryActive;

            let id = primaryActive?.itemId ?? '';

            if (secondaryActive) {
                id += `/${secondaryActive.itemId}`;
            }

            this.grid = this.gridFactory.fromResponse(`${this.config.id}/${id}`, fixtures, {
                grouping: gridType === TabbedGridType.CompetitionGrid ? GridGrouping.Date : GridGrouping.None,
                disableGroupSorting: disabledFixtureSorting,
                moreGrouping,
            });

            this.hasData = true;
            this.setWidgetLoaded();

            if (this.config.refreshable) {
                return;
            }
        };

        if (showNonGridable) {
            this.hasNonGridable()
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((hasNonGridable) => {
                    generateGrid(hasNonGridable);
                });
        } else {
            generateGrid();
        }
    }

    private hasNonGridable(): Observable<boolean> {
        const context = { ...this.contextService.getContext(), ...this.getWidgetContext() };

        if (!context.sportId) {
            return of(false);
        }

        return this.sportCache.getSport(context.sportId).pipe(
            map((sport) => {
                if (!sport) {
                    return false;
                }

                const countItem = this.getCountItem(context, sport);

                return (countItem?.counts.nonGridable ?? 0) > 0;
            }),
        );
    }

    private getCountItem(context: Partial<WidgetContext>, sport: CountItem): CountItem | undefined {
        if (context.virtualCompetitionGroupId) {
            return children(sport, ItemType.VirtualCompetitionGroup).find((item) => item.id === context.virtualCompetitionGroupId);
        }

        if (context.competitionId) {
            return children(sport, ItemType.Competition).find((item) => item.id === context.competitionId);
        }

        if (context.virtualCompetitionId) {
            return children(sport, ItemType.Competition).find((item) => item.isVirtual && item.id === context.virtualCompetitionId);
        }

        if (context.tournamentId) {
            return children(sport, ItemType.Tournament).find((item) => item.id === context.tournamentId);
        }

        if (context.regionId) {
            return children(sport, ItemType.Region).find((item) => item.id === context.regionId);
        }

        return children(sport, ItemType.Sport).find((item) => item.id === context.sportId);
    }

    private setTabs(payload: TabbedGridPayload): void {
        const [firstLevel, secondLevel] = this.navigationService.get(this.config.id, payload);

        this.primaryNavigation = firstLevel;
        this.secondaryNavigation = secondLevel;

        this.stretchTab = this.primaryNavigation?.[0]?.group?.type === GroupItemType.FixtureState;
    }

    private getDataForTitleIcon(fixture: Fixture): MatchListItem {
        switch (this.titleIcon) {
            case TitleIconOptions.SportIcon:
                return {
                    id: fixture.sport.id.toString(),
                    title: fixture.sport.name.value,
                    icon: `sports-${fixture.sport.id}`,
                };
            case TitleIconOptions.CompetitionLogo:
            case TitleIconOptions.CompetitionFlag:
                return {
                    id: fixture.competition.id.toString(),
                    title: fixture.competition.name.value,
                    icon: `country-icon c${fixture.region.id}`,
                    sportId: fixture.sport.id,
                };
            default:
                return { id: '', title: '' };
        }
    }

    private getPrimaryActive(): TabbedGridTabBarItem | undefined {
        return this.primaryNavigation.find((item) => item.active);
    }

    private getSecondaryActive(): TabbedGridTabBarItem | undefined {
        return this.secondaryNavigation.find((item) => item.active);
    }

    private setGridAndLinks(isSwitchOnFirstLevelTab: boolean): void {
        let loadingIndicator: ILoadingIndicatorHandler | undefined;

        if (this.matchListConfig.showLoader) {
            loadingIndicator = this.loadingIndicatorService.start(token);
        }

        this.contextService
            .getWidget<TabbedGridPayload>(this.getWidgetContext(isSwitchOnFirstLevelTab, true))
            .pipe(
                finalize(() => loadingIndicator?.done()),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(({ payload }) => {
                if (payload) {
                    if (isSwitchOnFirstLevelTab) {
                        const [, secondary] = this.navigationService.get(this.config.id, payload);
                        this.secondaryNavigation = secondary;
                    }
                    this.setGrid(payload);
                    this.setMarketSwitcherVisibility();
                    this.linkConfig = payload.bottomLink;
                    this.setLink(payload);
                    this.setTracking(payload.fixtures);
                    this.changeRef.markForCheck();
                }
            });
    }

    private getWidgetContext(primaryTabSwtich: boolean = false, tabSwitch: boolean = false): Partial<WidgetContext> {
        const primaryActive = this.getPrimaryActive();
        let secondaryActive;

        if (!primaryTabSwtich) {
            secondaryActive = this.getSecondaryActive();
        }

        let request = super.getContext();
        request = !this.shouldIncludePayload ? request : { ...request, shouldIncludePayload: this.shouldIncludePayload };

        if (primaryActive) {
            request = { ...request, ...primaryActive.context };
        }
        if (secondaryActive) {
            request = { ...request, ...secondaryActive.context };
        }

        if (tabSwitch) {
            if (secondaryActive) {
                const secondLevelTabIndex = this.secondaryNavigation.findIndex((tab) => tab.active);
                this.trackingService.trackTabSelected(secondaryActive, secondLevelTabIndex);
            } else if (primaryActive) {
                const firstLevelTabIndex = this.primaryNavigation.findIndex((tab) => tab.active);
                this.trackingService.trackTabSelected(primaryActive, firstLevelTabIndex);
            }
        }

        return request;
    }

    private setLink({ fixtures }: TabbedGridPayload): void {
        if (!this.linkConfig?.visible) {
            this.link = this.linkCount = this.linkTitle = undefined;

            return;
        }

        const primaryActive = this.getPrimaryActive();
        const secondaryActive = this.getSecondaryActive();

        if (!this.secondaryNavigation?.length) {
            this.activeNavigation = primaryActive;
        } else {
            this.activeNavigation = secondaryActive;

            if (this.activeNavigation?.group?.type === GroupItemType.FixtureState) {
                this.activeNavigation = primaryActive;
            }
        }

        if (this.activeNavigation) {
            const param =
                this.activeNavigation.group && this.activeNavigation.group.type !== GroupItemType.FixtureState
                    ? { id: this.activeNavigation.itemId, name: this.activeNavigation.group.name! }
                    : { id: this.activeNavigation.itemId, name: this.activeNavigation.itemId.toString() };

            this.setLinkAndTitle(param, fixtures?.[0]);
        }
    }

    private setLinkAndTitle(param: UrlParam, firstFixture: Fixture | undefined): void {
        const navigationInfo = this.widgetNavigationService.getNavigationInfo(param, this.linkConfig, firstFixture);
        this.link = navigationInfo?.link;
        this.linkTitle = navigationInfo?.title;
        if (this.linkConfig?.target === TabbedGridLinkTarget.SportLive || this.linkConfig?.target === TabbedGridLinkTarget.SportLobby) {
            this.linkCount = this.activeNavigation?.group?.count ?? 0;
        }
    }

    private setGlobalLink(firstFixture: Fixture): void {
        if (this.globalLinkConfig?.visible && firstFixture) {
            switch (this.globalLinkConfig.target) {
                case TabbedGridLinkTarget.SportLobby:
                    const param = {
                        id: firstFixture.sport.id,
                        name: firstFixture.sport.name.value,
                    };
                    this.globalLink = this.urlHelper.getSportUrl(param);
                    break;
                case TabbedGridLinkTarget.CompetitionLobby:
                    this.globalLink = this.widgetNavigationService.getCompetitionData(firstFixture);
                    break;
                default:
                    this.globalLink = this.globalLinkConfig.linkOverride ? this.globalLinkConfig.linkOverride : undefined;
            }
            this.globalLinkTitle = interpolateSingleBracesString(
                this.globalLinkConfig.title,
                {
                    sport: firstFixture.sport.name.value,
                    competition: firstFixture.competition.name.value,
                },
                false,
            );
        } else {
            this.globalLink = undefined;
            this.globalLinkTitle = undefined;
        }

        this.headerLinkConfig = {
            link: this.globalLink,
            linkText: this.globalLinkTitle,
        };
    }

    private setTracking(fixtures: Fixture[] | undefined): void {
        const firstLevelNavItemActiveTab = this.primaryNavigation?.find((tab) => tab.active);
        const secondLevelNavItemActiveTab = this.secondaryNavigation?.find((tab) => tab.active);
        if (secondLevelNavItemActiveTab) {
            this.tracking = this.trackingService.getTrackingValueForMatchList(secondLevelNavItemActiveTab);

            return;
        }
        if (firstLevelNavItemActiveTab) {
            this.tracking = this.trackingService.getTrackingValueForMatchList(firstLevelNavItemActiveTab);

            return;
        }
        if (fixtures?.length) {
            this.tracking = `sport/${fixtures?.[0].sport.id}`;
        }
    }

    private setGridTitleVisibility(currentOrientation: string) {
        const activePage = this.accessorService.getPage();
        if (!activePage) {
            return;
        }
        const isMarketPillsEnabled =
            this.gridConfig.isMarketPillsEnabled &&
            this.deviceService.isMobilePhone &&
            currentOrientation !== 'landscape' &&
            !this.hideMarketSwitcher;

        const homeLobbyTitleVisibility = isMarketPillsEnabled || this.gridConfig.hideGridTitle;
        const nonMatchlistTitleVisibility = activePage === WidgetPage.HomeLobby ? homeLobbyTitleVisibility : false;
        const isInComposable = this.parent?.type === WidgetType.Composable;

        if (this.gridType === TabbedGridType.CompetitionGrid) {
            this.hideGridTitle = true;
        } else if (isInComposable && this.isAllTabEnabled) {
            this.hideGridTitle = false;
        } else {
            this.hideGridTitle = this.gridType === TabbedGridType.MatchListGrid || nonMatchlistTitleVisibility;
        }
        this.changeRef.markForCheck();
    }

    private interpolateTitle(title: string, firstFixture: Fixture | undefined): string {
        const competition = firstFixture?.virtualCompetition?.name?.value ?? firstFixture?.competition?.name?.value ?? '';

        return interpolateDoubleBracesString(title, { competition });
    }

    private setHeaderChevronLink(showChevron: boolean, firstFixture: Fixture): void {
        if (this.primaryNavigation.length && !this.secondaryNavigation.length) {
            const urlParam: UrlParam =
                this.primaryNavigation.length === 1
                    ? {
                          id: this.primaryNavigation?.[0].itemId,
                          name: this.primaryNavigation?.[0].group?.name!,
                      }
                    : {
                          id: firstFixture.sport.id,
                          name: firstFixture.sport.name.value,
                      };
            const config: TabbedGridLink | undefined =
                this.primaryNavigation.length === 1 ? this.linkConfig : ({ target: TabbedGridLinkTarget.SportLobby } as TabbedGridLink);
            this.headerChevronLink = showChevron ? this.widgetNavigationService.getNavigationInfo(urlParam, config, firstFixture)?.link : undefined;
        }
    }

    headerNavigationHandler() {
        this.widgetNavigationService.headerNavigationHandler(this.headerChevronLink);
        this.trackingService.trackLinkClick({
            eventDetails: `${this.title}-chevron icon`,
            urlClicked: this.headerChevronLink,
        });
    }
}
